import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getDeviceInfo, reportLoginIssue, markSessionActive, clearSessionCanary, decodeTokenClaims, hasSessionCanary } from '../utils/deviceInfo';
import { readAdAttribution, clearAdAttribution } from '../utils/adAttribution';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [needsAgreement, setNeedsAgreement] = useState(false);
  const initialCheckDone = useRef(false);

  const getAuthHeaders = useCallback(() => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const checkAuth = useCallback(async (attempt = 0) => {
    const t = localStorage.getItem('token');

    const applySession = (newToken, userData) => {
      localStorage.setItem('token', newToken);
      try { localStorage.setItem('ccc-user-cache', JSON.stringify(userData)); } catch (_e) { /* noop */ }
      markSessionActive();
      setToken(newToken);
      setUser(userData);
      setNeedsAgreement(userData.terms_accepted === false);
      setLoading(false);
    };

    // Silent refresh: exchange the long-lived httpOnly refresh cookie for a fresh JWT.
    // This is the iOS-PWA recovery path — Safari evicts localStorage after ~7 days of
    // inactivity but keeps the server-set refresh cookie.
    const tryRefresh = async () => {
      try {
        const resp = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        if (resp.data?.token) {
          applySession(resp.data.token, resp.data.user);
          return true;
        }
      } catch { /* no/invalid refresh cookie */ }
      return false;
    };

    if (!t) {
      // localStorage token is gone. If a session was previously active (canary cookie
      // survived), try to silently restore it from the httpOnly refresh cookie.
      if (hasSessionCanary()) {
        const ok = await tryRefresh();
        if (ok) return;
        reportLoginIssue('token_missing_unexpected', 'localStorage token missing but canary present; silent refresh failed');
      }
      setUser(null);
      setToken(null);
      setLoading(false);
      return;
    }
    try {
      const resp = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
        withCredentials: true,
      });
      const { refreshed_token, ...userData } = resp.data;
      // Sliding session: backend issues a fresh token when the current one is >24h old
      if (refreshed_token) {
        localStorage.setItem('token', refreshed_token);
        setToken(refreshed_token);
      } else {
        setToken(t);
      }
      setUser(userData);
      setNeedsAgreement(userData.terms_accepted === false);
      try { localStorage.setItem('ccc-user-cache', JSON.stringify(userData)); } catch (_e) { /* noop */ }
      markSessionActive();
      setLoading(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // Server rejected the stored token. Before logging out, try a silent refresh
        // from the httpOnly cookie (handles an evicted/expired access token on PWA).
        const refreshed = await tryRefresh();
        if (refreshed) return;
        // If it hadn't expired yet, that's abnormal — auto-report diagnostics.
        const claims = decodeTokenClaims(t);
        const nowSec = Math.floor(Date.now() / 1000);
        const expired = claims?.exp ? claims.exp < nowSec : null;
        if (expired !== true) {
          reportLoginIssue('session_invalidated_401', `Server rejected a stored token (HTTP ${status}) that was ${expired === null ? 'undecodable' : 'NOT expired'}`, {
            token_issued_at: claims?.iat ? new Date(claims.iat * 1000).toISOString() : 'unknown',
            token_expires_at: claims?.exp ? new Date(claims.exp * 1000).toISOString() : 'unknown',
          });
        }
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
        localStorage.removeItem('ccc-user-cache');
        clearSessionCanary();
        setLoading(false);
      } else {
        // Network error (offline, PC just booted, server restarting):
        // retry with backoff while keeping the loading spinner, so the user
        // is never bounced to the login screen over a transient network drop.
        if (attempt < 2) {
          setTimeout(() => checkAuth(attempt + 1), (attempt + 1) * 1500);
          return;
        }
        // Still unreachable — restore the cached session optimistically.
        // It will be re-validated automatically when the connection returns.
        setToken(t);
        const cached = localStorage.getItem('ccc-user-cache');
        if (cached) {
          try {
            const userData = JSON.parse(cached);
            setUser(userData);
            setNeedsAgreement(userData.terms_accepted === false);
          } catch (_e) { /* noop */ }
        }
        setLoading(false);
      }
    }
  }, []);

  // Re-validate the session as soon as the connection comes back
  useEffect(() => {
    const onOnline = () => { if (localStorage.getItem('token')) checkAuth(); };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [checkAuth]);

  // Only run auth check ONCE on mount
  // CRITICAL: If returning from OAuth callback, skip the /me check.
  // AuthCallback will exchange the session_id and establish the session first.
  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (!initialCheckDone.current) {
      initialCheckDone.current = true;
      checkAuth();
    }
}, []);

  // Cancel Google One Tap prompt when user is already logged in
  useEffect(() => {
    if (user && window.google?.accounts?.id) {
      try {
        window.google.accounts.id.disableAutoSelect();
        window.google.accounts.id.cancel();
      } catch (_e) { /* noop */ }
    }
  }, [user]);

  const login = async (email, password, rememberMe = false) => {
    const resp = await axios.post(`${API}/auth/login`, { email, password, remember_me: rememberMe, device_info: getDeviceInfo() }, { withCredentials: true });
    localStorage.setItem('token', resp.data.token);
    try { localStorage.setItem('ccc-user-cache', JSON.stringify(resp.data.user)); } catch (_e) { /* noop */ }
    markSessionActive();
    setToken(resp.data.token);
    setUser(resp.data.user);
    window.dispatchEvent(new Event('auth-change'));
    // Precache app shell for offline access
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'PRECACHE_SHELL', routes: ['/dashboard', '/messaging', '/calendar', '/search', '/favorites', '/settings', '/resources', '/daily-report'] });
    }
    return resp.data.user;
  };

  const register = async (email, password, name, userType, careSegment = 'childcare') => {
    const attribution = readAdAttribution();
    const resp = await axios.post(`${API}/auth/register`, {
      email, password, name, user_type: userType, care_segment: careSegment,
      device_info: getDeviceInfo(),
      attribution: attribution || undefined,
    }, { withCredentials: true });
    localStorage.setItem('token', resp.data.token);
    try { localStorage.setItem('ccc-user-cache', JSON.stringify(resp.data.user)); } catch (_e) { /* noop */ }
    markSessionActive();
    setToken(resp.data.token);
    setUser(resp.data.user);
    // Attribution snapshot has served its purpose; clear it so a future retarget
    // campaign for the same browser starts a fresh first-touch record.
    if (attribution) clearAdAttribution();
    return resp.data.user;
  };

  const processGoogleCredential = async (credential) => {
    const resp = await axios.post(`${API}/auth/google-one-tap`, {
      credential,
      device_info: getDeviceInfo(),
    }, { withCredentials: true });
    localStorage.setItem('token', resp.data.token);
    try { localStorage.setItem('ccc-user-cache', JSON.stringify(resp.data.user)); } catch (_e) { /* noop */ }
    markSessionActive();
    setToken(resp.data.token);
    setUser(resp.data.user);
    window.dispatchEvent(new Event('auth-change'));
    return resp.data.user;
  };

  const processOAuthSession = async (sessionId) => {
    try {
      const resp = await axios.post(`${API}/auth/session`, { session_id: sessionId, device_info: getDeviceInfo() }, { withCredentials: true });
      if (resp.data.session_token) {
        localStorage.setItem('token', resp.data.session_token);
        setToken(resp.data.session_token);
      }
      // Set partial user immediately so ProtectedRoute doesn't redirect
      setUser(resp.data);
      // Then load full user document from /auth/me
      try {
        const meResp = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${resp.data.session_token}` },
          withCredentials: true,
        });
        if (meResp.data) {
          setUser(meResp.data);
        }
      } catch { /* partial user is fine as fallback */ }
      return resp.data;
    } catch (err) {
      // Extract detailed error from backend response for debugging
      const detail = err?.response?.data?.detail || err.message || 'unknown';
      console.error('processOAuthSession failed:', detail);
      const enrichedError = new Error(detail);
      enrichedError.detail = detail;
      throw enrichedError;
    }
  };

  const logout = async () => {
    // Cancel and disable Google One Tap to prevent auto re-login
    try {
      window.google?.accounts?.id?.disableAutoSelect();
      window.google?.accounts?.id?.cancel();
    } catch (_e) { /* noop */ }
    sessionStorage.setItem('ccc-just-logged-out', 'true');
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
    } catch { /* ignore */ }
    localStorage.removeItem('token');
    localStorage.removeItem('ccc-user-cache');
    clearSessionCanary();
    setToken(null);
    setUser(null);
    window.dispatchEvent(new Event('auth-change'));
  };

  const updateUser = (newData) => setUser(prev => prev ? { ...prev, ...newData } : prev);

  const acceptAgreement = async () => {
    try {
      await axios.post(`${API}/auth/accept-agreement`, {}, { headers: getAuthHeaders() });
      setNeedsAgreement(false);
      setUser(prev => prev ? { ...prev, terms_accepted: true } : prev);
    } catch (_e) { /* noop */ }
  };

  return (
    <AuthContext.Provider value={{
      user, loading, token, needsAgreement, login, register,
      processOAuthSession, processGoogleCredential, logout, getAuthHeaders, updateUser, checkAuth, acceptAgreement
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
